// First-person player controller.
// - PointerLock for mouse look
// - WASD + Shift(sprint) + Ctrl(crouch) + Space(jump)
// - Simple gravity + AABB collision with map walls
// - Exposes an imperative ref for the weapon/bots to raycast from.
import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { PointerLockControls } from "@react-three/drei";
import * as THREE from "three";
import { useSettings } from "@/stores/settingsStore";
import { useMatchStore } from "@/stores/matchStore";
import { resolveCollisions } from "@/game/systems/collision";
import { Audio_ } from "@/lib/audio";
import { getAgent } from "@/game/agents/agents";

const STAND_HEIGHT = 1.7;
const CROUCH_HEIGHT = 1.1;
const PLAYER_RADIUS = 0.45;
const GRAVITY = -22;
const JUMP_V = 7.2;
const WALK_SPEED = 5;
const SPRINT_SPEED = 7.8;
const CROUCH_SPEED = 2.4;

const keys = {
    forward: false, back: false, left: false, right: false,
    sprint: false, crouch: false, jump: false,
};

const Player = forwardRef(function Player({ walls, spawn, onPause }, ref) {
    const controlsRef = useRef();
    const { camera } = useThree();
    const settings = useSettings();

    // Persistent physics state
    const state = useRef({
        pos: new THREE.Vector3(spawn.position[0], spawn.position[1], spawn.position[2]),
        vel: new THREE.Vector3(0, 0, 0),
        onGround: true,
        height: STAND_HEIGHT,
        lastFootstep: 0,
        recoilKick: 0,
    });

    useImperativeHandle(ref, () => ({
        getPosition: () => state.current.pos.clone(),
        getEyePosition: () => state.current.pos.clone(),
        getForward: () => {
            const v = new THREE.Vector3();
            camera.getWorldDirection(v);
            return v;
        },
        applyRecoil: (amount) => {
            state.current.recoilKick = amount;
        },
        lock: () => controlsRef.current?.lock(),
        unlock: () => controlsRef.current?.unlock(),
        isLocked: () => !!controlsRef.current?.isLocked,
    }));

    // Camera FOV from settings
    useEffect(() => {
        camera.fov = settings.fov;
        camera.updateProjectionMatrix();
    }, [settings.fov, camera]);

    // Set initial camera position & yaw
    useEffect(() => {
        camera.position.copy(state.current.pos);
        camera.rotation.set(0, spawn.yaw ?? Math.PI, 0);
    }, [spawn, camera]);

    // Key events
    useEffect(() => {
        const down = (e) => {
            switch (e.code) {
                case "KeyW": keys.forward = true; break;
                case "KeyS": keys.back = true; break;
                case "KeyA": keys.left = true; break;
                case "KeyD": keys.right = true; break;
                case "ShiftLeft": case "ShiftRight": keys.sprint = true; break;
                case "ControlLeft": case "ControlRight": keys.crouch = true; break;
                case "Space": keys.jump = true; break;
                case "KeyR": useMatchStore.getState().startReload(); Audio_.play("reload", { volume: 0.5 }); break;
                case "Digit1": useMatchStore.getState().switchWeapon("rifle"); break;
                case "Digit2": useMatchStore.getState().switchWeapon("smg"); break;
                case "Digit3": useMatchStore.getState().switchWeapon("pistol"); break;
                case "Escape":
                    if (controlsRef.current?.isLocked) {
                        controlsRef.current.unlock();
                    }
                    onPause?.();
                    break;
                default: break;
            }
        };
        const up = (e) => {
            switch (e.code) {
                case "KeyW": keys.forward = false; break;
                case "KeyS": keys.back = false; break;
                case "KeyA": keys.left = false; break;
                case "KeyD": keys.right = false; break;
                case "ShiftLeft": case "ShiftRight": keys.sprint = false; break;
                case "ControlLeft": case "ControlRight": keys.crouch = false; break;
                case "Space": keys.jump = false; break;
                default: break;
            }
        };
        window.addEventListener("keydown", down);
        window.addEventListener("keyup", up);
        return () => {
            window.removeEventListener("keydown", down);
            window.removeEventListener("keyup", up);
        };
    }, [onPause]);

    useFrame((_, dt) => {
        const s = state.current;
        const match = useMatchStore.getState();
        if (match.phase !== "playing" || match.dead) return;

        // Active agent passive (movement + footstep modifiers)
        const agentId = useSettings.getState().selectedAgent || "recruit";
        const passive = getAgent(agentId).passive || {};
        const speedMult = passive.moveSpeedMult ?? 1;
        const footstepVolMult = passive.footstepVolumeMult ?? 1;

        dt = Math.min(dt, 0.05);

        // Height (crouch smooth)
        const targetHeight = keys.crouch ? CROUCH_HEIGHT : STAND_HEIGHT;
        s.height = THREE.MathUtils.lerp(s.height, targetHeight, Math.min(1, dt * 8));

        // Compute movement
        const speed = (keys.crouch
            ? CROUCH_SPEED
            : keys.sprint && keys.forward
              ? SPRINT_SPEED
              : WALK_SPEED) * speedMult;

        // Forward/right vectors on XZ plane from camera yaw
        const forward = new THREE.Vector3();
        camera.getWorldDirection(forward);
        forward.y = 0;
        forward.normalize();
        const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();

        const move = new THREE.Vector3();
        if (keys.forward) move.add(forward);
        if (keys.back) move.sub(forward);
        if (keys.right) move.add(right);
        if (keys.left) move.sub(right);
        if (move.lengthSq() > 0) move.normalize().multiplyScalar(speed);

        s.vel.x = move.x;
        s.vel.z = move.z;

        // Jump / gravity
        if (keys.jump && s.onGround) {
            s.vel.y = JUMP_V;
            s.onGround = false;
        }
        s.vel.y += GRAVITY * dt;

        // Apply velocity
        s.pos.x += s.vel.x * dt;
        s.pos.z += s.vel.z * dt;
        s.pos.y += s.vel.y * dt;

        // Ground collision (floor at y=0)
        if (s.pos.y <= 0) {
            s.pos.y = 0;
            s.vel.y = 0;
            s.onGround = true;
        }

        // Wall collisions (XZ plane)
        resolveCollisions(s.pos, PLAYER_RADIUS, walls);

        // Update camera position with head bob when moving
        const moving = move.lengthSq() > 0.01 && s.onGround;
        const t = performance.now() / 1000;
        const bobY = moving ? Math.sin(t * (keys.sprint ? 14 : 10)) * (keys.sprint ? 0.06 : 0.03) : 0;
        const bobX = moving ? Math.cos(t * (keys.sprint ? 7 : 5)) * 0.02 : 0;

        camera.position.set(
            s.pos.x + bobX,
            s.pos.y + s.height + bobY,
            s.pos.z
        );

        // Recoil kick decay
        if (s.recoilKick > 0) {
            camera.rotation.x += s.recoilKick;
            s.recoilKick *= 0.4;
            if (s.recoilKick < 0.001) s.recoilKick = 0;
        }

        // Footsteps
        if (moving && t - s.lastFootstep > (keys.sprint ? 0.35 : 0.5)) {
            s.lastFootstep = t;
            Audio_.play("footstep", { volume: (keys.crouch ? 0.15 : 0.35) * footstepVolMult });
        }
    });

    // Mouse sensitivity — mutate the controls object after lock
    useEffect(() => {
        const ctl = controlsRef.current;
        if (!ctl) return;
        // Drei's PointerLockControls internally uses its own pointerSpeed prop via drei
        if (ctl.pointerSpeed !== undefined) ctl.pointerSpeed = settings.mouseSensitivity;
    }, [settings.mouseSensitivity]);

    return (
        <PointerLockControls
            ref={controlsRef}
            pointerSpeed={settings.mouseSensitivity}
        />
    );
});

export default Player;
